import React, { memo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { MemoryRouter as Router, Switch, Route } from 'react-router-dom';

import {
  helpContentsSelector,
  helpLoadingSelector,
  pageHelpLoadingSelector,
  previousSectionSelector,
  helpAddingSelector,
  helpUpdatingSelector,
  helpContentDeletingSelector,
  contentsForExportLoadingSelector,
  contentsForExportSelector,
  sendingContentsForImportSelector,
} from '../shared/store/selectors';
import { releaseNotesSelector } from './subsections/releaseNotes/store/selectors';

import { userSelector } from '../../shared/store/selectors';
import isHelpEditable from './utils/isHelpEditable';
import { HelpContentPropTypes } from '../shared/proptypes';
import { UserWithoutActiveFlagPropTypes } from '../../shared/propTypes/users';
import {
  createHelpContent,
  deleteHelpContent,
  fetchAllContentsForExport,
  resetContentsForExport,
  sendContentsForImport,
} from '../shared/store/actions';
import { errorNotification } from '../../shared/store/notificationActions';
import MainPage from './mainPage.component';
import SectionPage from './sectionPage.component';
import HelpContent from '../helpContent/helpContent.component';
import { SearchDocsPage } from './subsections/searchDocs';
import { ReleaseNotes } from './subsections/releaseNotes';
import HelpContentNotFound from '../helpContent/helpContentNotFound.component';

const PageHelpContainer = ({
  helpContents,
  releaseNotes,
  helpLoading,
  pageHelpLoading,
  createHelpContent,
  helpAdding,
  deleteHelpContent,
  contentDeleting,
  user,
  toggleHelpModal,
  contentsForExport,
  contentsForExportLoading,
  fetchAllContentsForExport,
  resetContentsForExport,
  sendContentsForImport,
  sendingContentsForImport,
  errorNotification,
}) => {
  const contentEditable = isHelpEditable(user);

  return (
    <Router>
      <Switch>
        <Route
          exact
          path="/"
          // eslint-disable-next-line react/jsx-no-bind
          render={() => (
            <MainPage
              helpContents={helpContents}
              addContent={createHelpContent}
              addContentLoading={helpAdding}
              deleteContent={deleteHelpContent}
              deleteContentLoading={contentDeleting}
              helpLoading={helpLoading}
              pageHelpLoading={pageHelpLoading}
              onClose={toggleHelpModal}
              contentEditable={contentEditable}
              contentsForExport={contentsForExport}
              contentsForExportLoading={contentsForExportLoading}
              fetchAllContentsForExport={fetchAllContentsForExport}
              resetContentsForExport={resetContentsForExport}
              sendContentsForImport={sendContentsForImport}
              sendingContentsForImport={sendingContentsForImport}
              errorNotification={errorNotification}
            />
          )}
        />
        <Route
          path={`/section/:sectionName`}
          // eslint-disable-next-line react/jsx-no-bind
          render={({ match, history }) => (
            <SectionPage
              sectionName={match.params.sectionName}
              helpContents={helpContents}
              addContent={createHelpContent}
              addContentLoading={helpAdding}
              deleteContent={deleteHelpContent}
              deleteContentLoading={contentDeleting}
              pageHelpLoading={pageHelpLoading}
              onClose={toggleHelpModal}
              history={history}
              contentEditable={contentEditable}
            />
          )}
        />
        <Route
          path={`/releaseNotes`}
          // eslint-disable-next-line react/jsx-no-bind
          render={({ history }) => (<ReleaseNotes
            history={history}
            onClose={toggleHelpModal}
            contentEditable={contentEditable}
          />
          )}
        />
        <Route
          path={`/search`}
          // eslint-disable-next-line react/jsx-no-bind
          render={({ history }) => (<SearchDocsPage history={history} onClose={toggleHelpModal} />)}
        />
        <Route
          path={`/not-found`}
          // eslint-disable-next-line react/jsx-no-bind
          render={({ history }) => (<HelpContentNotFound history={history} onClose={toggleHelpModal} />)}
        />
        {helpContents.map(helpItem => (
          <Route
            key={helpItem.contentId}
            path={`/content/${helpItem.contentId}`}
            // eslint-disable-next-line react/jsx-no-bind
            render={({ history }) => (
              <HelpContent
                helpContentId={helpItem.contentId}
                helpContentTitle={helpItem.title}
                readOnly={!contentEditable}
                history={history}
                onClose={toggleHelpModal}
              />
            )}
          />
        ))}
        {releaseNotes.map(releaseNote => (
          <Route
            key={releaseNote.contentId}
            path={`/content/${releaseNote.contentId}`}
            // eslint-disable-next-line react/jsx-no-bind
            render={({ history }) => (
              <HelpContent
                helpContentId={releaseNote.contentId}
                helpContentTitle={releaseNote.title}
                readOnly={!contentEditable}
                history={history}
                onClose={toggleHelpModal}
              />
            )}
          />
        ))}
        <Route
          path="/content/:contentId"
          // eslint-disable-next-line react/jsx-no-bind
          render={({ history, match }) => (
            <HelpContent
              helpContentId={parseInt(match.params.contentId, 10)}
              readOnly={!contentEditable}
              history={history}
              onClose={toggleHelpModal}
            />
          )}
        />
      </Switch>
    </Router>
  );
};

PageHelpContainer.propTypes = {
  helpContents: PropTypes.arrayOf(HelpContentPropTypes).isRequired,
  releaseNotes: PropTypes.arrayOf(HelpContentPropTypes).isRequired,
  toggleHelpModal: PropTypes.func.isRequired,
  helpLoading: PropTypes.bool.isRequired,
  pageHelpLoading: PropTypes.bool.isRequired,
  createHelpContent: PropTypes.func,
  deleteHelpContent: PropTypes.func,
  helpAdding: PropTypes.bool,
  contentDeleting: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
  ]),
  user: UserWithoutActiveFlagPropTypes,
  contentsForExport: PropTypes.arrayOf(PropTypes.shape({
    contentId: PropTypes.number.isRequired,
    icon: PropTypes.string,
    section: PropTypes.string,
    title: PropTypes.string.isRequired,
    content: PropTypes.string,
    contentText: PropTypes.string,
    pageUrl: PropTypes.string,
    pageName: PropTypes.string,
  })).isRequired,
  contentsForExportLoading: PropTypes.bool.isRequired,
  fetchAllContentsForExport: PropTypes.func.isRequired,
  resetContentsForExport: PropTypes.func.isRequired,
  sendContentsForImport: PropTypes.func.isRequired,
  sendingContentsForImport: PropTypes.bool.isRequired,
  errorNotification: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    helpContents: helpContentsSelector(state),
    releaseNotes: releaseNotesSelector(state),
    helpLoading: helpLoadingSelector(state),
    pageHelpLoading: pageHelpLoadingSelector(state),
    user: userSelector(state),
    previousSection: previousSectionSelector(state),
    helpAdding: helpAddingSelector(state),
    helpUpdating: helpUpdatingSelector(state),
    contentDeleting: helpContentDeletingSelector(state),
    contentsForExport: contentsForExportSelector(state),
    contentsForExportLoading: contentsForExportLoadingSelector(state),
    sendingContentsForImport: sendingContentsForImportSelector(state),
  };
}

export default memo(connect(
  mapStateToProps,
  {
    createHelpContent,
    deleteHelpContent,
    fetchAllContentsForExport,
    resetContentsForExport,
    sendContentsForImport,
    errorNotification,
  }
)(PageHelpContainer));
